import { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { PreliTransactionItem, TransactionsProps } from "./types";
import "./transactions.scss";
import {
  PRELIQUIDATION_TRANSACTIONS_LABELS,
  DATE_FORMATS,
  DETAIL_PRICING_TRANSACTIONS,
  PRELI_TRANSACTIONS_DETAILS_KM_TYPES,
  PRELI_TRANSACTIONS_ADJUSTMENT_PRESET_REASONS,
} from "utils/constants";
import "../i18n";
import { useTranslation } from "react-i18next";
import { Collapsible } from "@pickit/pickit-components";
import classNames from "classnames";
import { RatesPreliquidationType } from "reducers/types/preliquidation";

export const Transactions = ({
  total,
  trips,
  adjustments,
  testId,
}: TransactionsProps) => {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(-1);
  const [preliTransactions, setPreliTransactions] = useState<
    PreliTransactionItem[]
  >([]);

  const [transactionCount, setTransactionCount] = useState(0);

  const onItemClicked = (index: number) => {
    setCurrent((prev) => (prev === index ? -1 : index));
  };

  useEffect(() => {
    const items: PreliTransactionItem[] = [];
    setTransactionCount(0);
    trips.forEach((trip) => {
      setTransactionCount((prev) =>
        trip.transactionCount ? prev + trip.transactionCount : prev
      );
      const item: PreliTransactionItem = {
        id: trip.id,
        date: moment(trip.finishedAt).format(DATE_FORMATS.shortDate),
        dateISO: trip.finishedAt,
        status: t("presettlement:finished"),
        value: trip.totalEarnings,
        tripDetails: trip.tripRates,
        transactionCount: trip.transactionCount,
      };
      items.push(item);
    });
    adjustments.forEach((adjustment) => {
      const defaultStatus =
        adjustment.price > 0
          ? t("detailPresettlement:plus")
          : t("detailPresettlement:subtract");
      const item: PreliTransactionItem = {
        date: moment(adjustment.createdAt).format(DATE_FORMATS.shortDate),
        dateISO: adjustment.createdAt,
        status: PRELI_TRANSACTIONS_ADJUSTMENT_PRESET_REASONS.includes(
          adjustment.reason
        )
          ? adjustment.reason
          : defaultStatus,
        value: adjustment.price,
      };
      items.push(item);
    });
    items.sort((a, b) => {
      const dateA = moment(a.dateISO);
      const dateB = moment(b.dateISO);
      return dateA.isBefore(dateB, "seconds") ? 1 : -1;
    });
    setPreliTransactions(items);
  }, [adjustments, t, trips]);

  const renderTrip = useCallback(
    (trip: PreliTransactionItem, index: number) => {
      return (
        <div
          className="container-detail-preliquidation-table-row"
          key={trip.id || index}
        >
          <div className="container-detail-preliquidation-table-col-sm-2 detail-preliquidation-table-body">
            <div className="detailIconContainer">
              <p data-testId={`${testId}-trip-id-${index}`}>{trip.id || "-"}</p>
            </div>
          </div>
          <div className="container-detail-preliquidation-table-col-sm-2 detail-preliquidation-table-body">
            <p data-testId={`${testId}-finish-at-${index}`}>
              {trip.date ? trip.date : ""}
            </p>
          </div>
          <div className="container-detail-preliquidation-table-col-sm-3 detail-preliquidation-table-body">
            <p data-testId={`${testId}-transaction-count-${index}`}>
              {trip.transactionCount
                ? `${trip.transactionCount} ${
                    trip.transactionCount === 1
                      ? t("detailPresettlement:package")
                      : t("detailPresettlement:packages")
                  }`
                : ""}
            </p>
          </div>
          <div className="container-detail-preliquidation-table-col-sm-3 detail-preliquidation-table-body">
            <p data-testId={`${testId}-status-tag-${index}`}>{trip.status}</p>
          </div>
          <div className="container-detail-preliquidation-table-col-sm-3 detail-preliquidation-table-body">
            <p
              data-testId={`${testId}-amount-${index}`}
              className={classNames({
                "amount-plus": !trip.tripDetails && trip.value > 0,
                "amount-subtract": !trip.tripDetails && trip.value < 0,
              })}
            >
              {`$ ${Math.abs(trip.value)}`}
            </p>
          </div>
        </div>
      );
    },
    [t, testId]
  );

  const renderTransactionDetail = useCallback(
    (details: RatesPreliquidationType[], index: number) => (
      <div>
        {details.map((detail: RatesPreliquidationType) => (
          <div className="container-detail-preliquidation-table-row transaction-detail">
            <div
              data-testId={`${testId}-detail-amount-${index}`}
              className="container-detail-preliquidation-table-col-offset-4 container-detail-preliquidation-table-col-3 detail-preliquidation-table-body-detail"
            >
              {detail.description &&
                `${
                  !PRELI_TRANSACTIONS_DETAILS_KM_TYPES.includes(detail.type)
                    ? "x"
                    : ""
                }${detail.description}`}
            </div>
            <div
              data-testId={`${testId}-detail-description-${index}`}
              className="container-detail-preliquidation-table-col-3 detail-preliquidation-table-body-detail"
            >
              {t(DETAIL_PRICING_TRANSACTIONS[detail.type])}
            </div>
            <div
              data-testId={`${testId}-detail-value-${index}`}
              className="container-detail-preliquidation-table-col-3 detail-preliquidation-table-body-detail"
            >
              {`$ ${detail.value.toFixed(2)}`}
            </div>
          </div>
        ))}
      </div>
    ),
    [t, testId]
  );

  return (
    <>
      <div className="container-detail-preliquidation-table-col">
        <div className="container-detail-preliquidation-table-row titles-container">
          {PRELIQUIDATION_TRANSACTIONS_LABELS.map((label, index) => (
            <div
              key={label}
              className={`container-detail-preliquidation-table-col-sm-${
                index === 0 || index === 1 ? 2 : 3
              } detail-preliquidation-table-head`}
              data-testId={`${testId}-table-title-${label.replace(`presettlement:`, '').toLowerCase()}`}
            >
              <p>{t(label)}</p>
            </div>
          ))}
        </div>
        <div className="detail-preliquidation-table-content overflow-scroll">
          {preliTransactions.map((transaction, index) => (
            <div className="detail-preliquidation-transaction">
              {transaction.tripDetails && transaction.tripDetails.length > 0 ? (
                <Collapsible
                  externalControl
                  open={current === index}
                  onClick={() => onItemClicked(index)}
                  testId={`${testId}-row-${index + 1}`}
                >
                  {renderTrip(transaction, index)}
                  {renderTransactionDetail(transaction.tripDetails, index)}
                </Collapsible>
              ) : (
                <div className="padding-right">
                  {renderTrip(transaction, index)}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="container-detail-preliquidation-table-row">
          <div className="row-transparent"></div>
          <div className="container-detail-preliquidation-table-col-sm-10 ">
            <p
              data-testId={`${testId}-total-label`}
              className="transaction-total-text"
            >
              {t("presettlement:total")}
            </p>
            <p
              data-testId={`${testId}-total-label-quantity`}
              className="transaction-quantity"
            >
              {`${transactionCount} ${t(
                transactionCount > 1
                  ? "presettlement:transactions"
                  : "presettlement:transaction"
              )}`}
            </p>
          </div>
          <div className="container-detail-preliquidation-table-col-sm-3 amount-container">
            <p data-testId={`${testId}-total`}>{`$ ${total}`}</p>
          </div>
        </div>
      </div>
    </>
  );
};
